import { useNavigate, useLocation } from "react-router-dom";
import { Home, Briefcase, MapPin, Camera, Plus, Zap, FileText, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { icon: Home, label: "Home", path: "/" },
    { icon: Briefcase, label: "Jobs", path: "/jobs" },
    { icon: MapPin, label: "Canvass", path: "/canvassing" },
    { icon: Camera, label: "Field", path: "/field-capture" },
  ];

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const NavBtn = ({ item }: { item: (typeof navItems)[number] }) => (
    <button
      key={item.path}
      onClick={() => navigate(item.path)}
      className={cn(
        "relative flex-1 flex flex-col items-center justify-center gap-1 h-14 transition-colors",
        isActive(item.path) ? "text-primary" : "text-muted-foreground hover:text-foreground"
      )}
    >
      {isActive(item.path) && (
        <span className="absolute top-0 left-1/2 -translate-x-1/2 h-0.5 w-8 rounded-full bg-primary shadow-[0_0_8px_hsl(var(--primary))]" />
      )}
      <item.icon className="h-5 w-5" strokeWidth={isActive(item.path) ? 2.5 : 2} />
      <span className="text-[10px] font-medium tracking-wide">{item.label}</span>
    </button>
  );

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-border/40 bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <div className="relative flex items-center justify-around max-w-2xl mx-auto px-2">
        <NavBtn item={navItems[0]} />
        <NavBtn item={navItems[1]} />

        {/* Center Quick Action — floating */}
        <div className="relative w-16 flex items-center justify-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                className="h-14 w-14 -mt-6 rounded-full shadow-[0_8px_24px_-4px_hsl(var(--primary)/0.6)] ring-4 ring-background"
              >
                <Plus className="h-6 w-6" strokeWidth={2.5} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" side="top" sideOffset={12} className="w-56 p-1">
              <DropdownMenuItem onClick={() => navigate("/measurements")} className="py-3 gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 text-primary">
                  <Zap className="h-4 w-4" />
                </span>
                <span className="flex flex-col items-start">
                  <span className="text-sm font-semibold">Instant Quote</span>
                  <span className="text-[11px] text-muted-foreground">Full claims quick quote</span>
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/field-capture")} className="py-3 gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-foreground">
                  <Camera className="h-4 w-4" />
                </span>
                <span className="flex flex-col items-start">
                  <span className="text-sm font-semibold">New Inspection</span>
                  <span className="text-[11px] text-muted-foreground">Photos, video, scout</span>
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/estimates/new")} className="py-3 gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-foreground">
                  <FileText className="h-4 w-4" />
                </span>
                <span className="flex flex-col items-start">
                  <span className="text-sm font-semibold">New Estimate</span>
                  <span className="text-[11px] text-muted-foreground">Build full proposal</span>
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/leads/new")} className="py-3 gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-foreground">
                  <ClipboardList className="h-4 w-4" />
                </span>
                <span className="flex flex-col items-start">
                  <span className="text-sm font-semibold">Log Lead</span>
                  <span className="text-[11px] text-muted-foreground">Quick capture form</span>
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <NavBtn item={navItems[2]} />
        <NavBtn item={navItems[3]} />
      </div>
    </div>
  );
}
